import {
    CrownOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
} from "@ant-design/icons";
import { Spin } from "antd";
import { memo, useCallback, useMemo } from "react";
import { AnyTask } from "tm-shared/types";

import { QueueTaskStatus } from "@/hooks/core/useAsyncTaskQueue";
import { useOfflineModeSelector } from "@/hooks/redux";
import { PrimaryText } from "@/shared/ui/Typography";

import { StyledCheckbox, StyledContainer, StyledMBTaskLabel } from "./Styled";

interface PointTaskItemProps {
    task: AnyTask;
    isTaskEnabled: (task: AnyTask) => boolean;
    onTaskClick: (task: AnyTask) => void;
    isTaskDirty: boolean;
    accent?: boolean;
    status?: QueueTaskStatus | null;
}

const PointTaskItemInner = ({
    task,
    isTaskEnabled,
    onTaskClick,
    accent = false,
    status = null,
    isTaskDirty = false,
}: PointTaskItemProps) => {
    const offlineMode = useOfflineModeSelector();

    const isActive = useMemo(() => isTaskEnabled(task), [isTaskEnabled, task]);

    const { name, completed, info } = task;

    const isCompletedNotByAgent =
        completed &&
        info?.realPerformer?.userId &&
        info.realPerformer.userId !== info?.performer?.userId;

    const handleClick = useCallback(() => {
        onTaskClick(task);
    }, [onTaskClick, task]);

    const renderIndicator = useCallback(() => {
        if (!offlineMode) {
            if (status === QueueTaskStatus.Error) {
                return (
                    <PrimaryText fontSize={18} textColor="error">
                        <InfoCircleOutlined />
                    </PrimaryText>
                );
            }

            if (status === QueueTaskStatus.Pending) {
                return (
                    <Spin
                        indicator={
                            <LoadingOutlined style={{ fontSize: 18 }} spin />
                        }
                    />
                );
            }
        }

        return (
            <StyledCheckbox
                disabled
                checked={completed}
                aria-checked={completed}
                aria-disabled
                $isActive={isActive}
                $pending={Boolean(status)}
            />
        );
    }, [offlineMode, status, completed, isActive]);

    return (
        <StyledContainer $accent={accent}>
            {renderIndicator()}

            <StyledMBTaskLabel
                type="text"
                role="button"
                tabIndex={0}
                onClick={handleClick}
                $isActive={isActive}
                $isTaskDirty={isTaskDirty}
            >
                {name}

                {isCompletedNotByAgent && (
                    <PrimaryText fontSize={20} textColor="warning">
                        <CrownOutlined />
                    </PrimaryText>
                )}
            </StyledMBTaskLabel>
        </StyledContainer>
    );
};

export const PointTaskItem = memo(PointTaskItemInner);

PointTaskItem.displayName = "PointTaskItem";
